import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { Eye, EyeOff, LogIn } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wasDeleted = searchParams.get('deleted') === '1';
  // iter382: a link from an email (for example the drift alert's
  // "Opna sundurliðun á verkinu") lands here when the session has expired.
  // `?next=` is carried through the login and opened afterwards, so the
  // founder is not dumped on the dashboard with no way back.
  const nextPath = (() => {
    const raw = searchParams.get('next') || '';
    // Only same-site paths, never an absolute URL.
    return raw.startsWith('/') && !raw.startsWith('//') ? raw : '';
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const userData = await login(email, password);
      if (nextPath) {
        navigate(nextPath);
      } else if (userData?.role === 'superadmin') {
        navigate('/platform');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding (desktop) */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-background-elevated border-r border-border">
        <div className="flex flex-col justify-center px-16">
          <img 
            src="/logos/logo-white.png" 
            alt="REKTRA" 
            className="h-24 w-auto object-contain mb-8"
            data-testid="login-logo-desktop"
          />
          <p className="text-foreground-secondary text-lg leading-relaxed max-w-md">
            {t('login.tagline')}
          </p>
          
          <p className="mt-6 text-foreground-muted text-sm leading-relaxed max-w-md">
            {t('login.description')}
          </p>

          <div className="mt-8 space-y-3 text-sm text-foreground-muted">
            {[t('login.bullet_1'), t('login.bullet_2'), t('login.bullet_3'), t('login.bullet_4')].map((text, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-1 h-1 rounded-full bg-accent flex-shrink-0" />
                <span>{text}</span>
              </div>
            ))}
          </div>

          <p className="mt-4 text-foreground-muted/60 text-xs">
            {t('login.no_setup')}
          </p>

          <p className="mt-6 text-foreground-muted text-xs">
            {t('login.designed_for')}
          </p>

          <p className="mt-8 text-foreground-muted text-sm leading-relaxed max-w-md">
            {t('login.all_in_one')}
          </p>

          <p className="mt-4 text-foreground-secondary text-xs font-medium uppercase tracking-wide">
            {t('login.for_icelandic')}
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10 text-center">
            <img 
              src="/logos/logo-white.png" 
              alt="REKTRA" 
              className="h-16 w-auto object-contain mx-auto"
              data-testid="login-logo-mobile"
            />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-2">{t('login.heading')}</h2>
            <p className="text-foreground-secondary">{t('login.subtitle')}</p>
          </div>

          {wasDeleted && (
            <div className="mb-6 p-4 bg-foreground-muted/10 border border-border rounded-lg text-sm text-foreground-muted" data-testid="deleted-notice">
              {t('login.company_deleted')}
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg text-error text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-2">
                {t('login.email_label')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.email_placeholder')}
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-2">
                {t('login.password_label')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                  className="pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-xs text-foreground-muted hover:text-accent transition-colors" data-testid="forgot-password-link">
                  {t('login.forgot_password')}
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn btn-primary btn-lg"
              data-testid="login-submit"
            >
              {isLoading ? (
                <div className="spinner w-5 h-5" />
              ) : (
                <>
                  <LogIn size={18} />
                  {t('login.submit')}
                </>
              )}
            </button>
          </form>

          <div className="my-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-sm text-foreground-muted">{t('login.or')}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <button
            onClick={handleGoogleLogin}
            className="w-full btn btn-secondary btn-lg"
            data-testid="login-google"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('login.google_btn')}
          </button>

          <p className="mt-8 text-center text-sm text-foreground-muted">
            {t('login.new_to_rektra')}{' '}
            <Link to="/register" className="text-accent hover:underline" data-testid="register-link">
              {t('login.register_link')}
            </Link>
          </p>

          <div className="mt-6 text-center text-xs text-foreground-muted">
            <Link to="/terms" className="hover:text-accent">{t('login.terms')}</Link>
            <span className="mx-2">&middot;</span>
            <Link to="/privacy" className="hover:text-accent">{t('login.privacy')}</Link>
            <span className="mx-2">&middot;</span>
            <Link to="/refund" className="hover:text-accent">{t('login.refund')}</Link>
            <span className="mx-2">&middot;</span>
            <Link to="/verd" className="hover:text-accent">{t('login.pricing')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
